import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { loadRFXRequest } from 'gql';
import { RFICorrespondence, RFIRequestForm, RFIRequestInformation } from 'interfaces/RFIRequestForm';
import {
  RequestOnChangeParam,
  RFXRequestAction,
  RFXRequestDocuments,
  RFXRequestState
} from 'interfaces/RFXRequestForm';
import { cloneDeep } from 'lodash';
import { AppDispatch, RootState } from 'store';
import { UIInput } from 'types/uiTypes';
import { convertSystemErrorToString } from 'util/errorUtils';
import { convertRFXRequestToRFIRequestForm } from 'util/transformers';

import { WizardProps } from '@amzn/awsui-components-react-v3';

import { NEW_RFI_REQUEST_FORM, RFIWizardStep } from './constants';
import { cloneRfiRequestForm, createNewRfiRequestForm } from './helper';
import { NavigationHelper } from './navigationHelper';
import {
  ErrorKey,
  getErrorMessage,
  validateCorrespondence,
  validateGeneralInformation,
  validateSuppliers
} from './validators';

export const SLICE_NAME = 'rfiWizard';

/**
 * Validations errors for one RFI step.
 * This interface is designed to make it easy to show errors in navigation error dialog.
 */
export interface RFIStepValidationErrors {
  hasError: boolean;
  errors: string[];
}

/**
 * Validations errors for all RFI steps.
 *
 * Inside this redux slice, we only save error keys because differnt UI will show different
 * messages. We don't want to save messages in redux store.
 */
export interface RFIValidationErrors {
  generalInformationStep: ErrorKey[];
  correspondenceStep: ErrorKey[];
  supplierStep: ErrorKey[];
}

const initialValidationErrorKeys: RFIValidationErrors = {
  generalInformationStep: [],
  correspondenceStep: [],
  supplierStep: []
};

export interface RFIWizardState {
  requestAction: RFXRequestAction;
  // Is new request if the RFI is new created and has never been saved.
  isNewRequest: boolean;
  activeStepIndex: number;
  wizardData: RFIRequestForm;
  errors: {
    validationErrors: RFIValidationErrors;
    navigationError: string;
    loadingRFIForEditError: string;
  };
  status: {
    // Whether RFI wizard is loading the next step. This is triggered by wizard navigation.
    isLoadingNextStep: boolean;
    // Whether the "EditRFIRequest" page is loading. This is triggered by clicking
    // "Edit current round" from buyer details view.
    isLoadingRFIForEdit: boolean;
  };
  visibility: {
    // In review step, if buyer click sumbit button, this will be set to true and show a modal to
    // notify user of re-direct to details view page, etc.
    isSubmitModalVisible: boolean;
  };
}

export interface OnRFIInformationChangeParam {
  field: keyof RFIRequestInformation;
  value: RFIRequestInformation[keyof RFIRequestInformation];
}

export interface OnRFICorrespondenceChangeParam {
  field: keyof RFICorrespondence;
  value: RFICorrespondence[keyof RFICorrespondence];
}
export interface OnRFXDocumentsChangeParam {
  field: keyof RFXRequestDocuments;
  value: RFXRequestDocuments[keyof RFXRequestDocuments];
}

export interface OnRFICorrespondenceChangeParam {
  field: keyof RFICorrespondence;
  value: RFICorrespondence[keyof RFICorrespondence];
}

export interface SetRFIWizardErrorParam {
  errorKey: keyof RFIWizardState['errors'];
  error: RFIWizardState['errors'][keyof RFIWizardState['errors']];
}

export interface SetRFIWizardStepErrorParam {
  stepKey: keyof RFIValidationErrors;
  errors: ErrorKey[];
}

export interface SetRFIWizardStatusParam {
  statusKey: keyof RFIWizardState['status'];
  status: RFIWizardState['status'][keyof RFIWizardState['status']];
}

export interface SetRFIWizardVisibilityParam {
  visibilityKey: keyof RFIWizardState['visibility'];
  visibility: RFIWizardState['visibility'][keyof RFIWizardState['visibility']];
}

const initialState: RFIWizardState = {
  requestAction: RFXRequestAction.CREATE_REQUEST,
  isNewRequest: true,
  activeStepIndex: RFIWizardStep.GeneralInformation,
  wizardData: NEW_RFI_REQUEST_FORM,
  errors: {
    validationErrors: initialValidationErrorKeys,
    navigationError: '',
    loadingRFIForEditError: ''
  },
  status: {
    isLoadingNextStep: false,
    isLoadingRFIForEdit: false
  },
  visibility: { isSubmitModalVisible: false }
};

/**
 * Slice of RFI wizard and buyer details view
 */
const rfiWizardSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    createNewRFIRequest(state, action: PayloadAction<{ businessId: string; buyer: string }>) {
      const { businessId, buyer } = action.payload;
      return {
        ...initialState,
        wizardData: createNewRfiRequestForm({ businessId, buyer })
      };
    },
    // Clone RFI request
    // Pre-condition: buyer should belong to the same business of the current RFI
    cloneRFIRequest(state, action: PayloadAction<{ buyer: string; rfiRequestForm: RFIRequestForm }>) {
      const { buyer, rfiRequestForm } = action.payload;
      const currentRFI = cloneDeep(rfiRequestForm);
      return {
        ...initialState,
        requestAction: RFXRequestAction.CLONE_REQUEST,
        wizardData: cloneRfiRequestForm({ currentRFI, buyer })
      };
    },
    setRFIRequestState(state, action: PayloadAction<RFXRequestState>) {
      state.wizardData.state = action.payload;
    },
    setRFIActiveStepIndex(state, action: PayloadAction<number>) {
      state.activeStepIndex = action.payload;
    },
    setRFIIsNew(state, action: PayloadAction<boolean>) {
      state.isNewRequest = action.payload;
    },
    setRFIWizardData(state, action: PayloadAction<RFIRequestForm>) {
      return {
        ...state,
        wizardData: action.payload
      };
    },
    setRFIWizardError(state, action: PayloadAction<SetRFIWizardErrorParam>) {
      const { errorKey, error } = action.payload;
      return {
        ...state,
        errors: {
          ...state.errors,
          [errorKey]: error
        }
      };
    },
    // Set validation error for one step
    setRFIWizardStepError(state, action: PayloadAction<SetRFIWizardStepErrorParam>) {
      const { stepKey, errors } = action.payload;
      return {
        ...state,
        errors: {
          ...state.errors,
          validationErrors: {
            ...state.errors.validationErrors,
            [stepKey]: errors
          }
        }
      };
    },
    // Clear validation error for one step
    clearRFIWizardStepError(state, action: PayloadAction<{ stepKey: keyof RFIValidationErrors }>) {
      const { stepKey } = action.payload;
      return {
        ...state,
        errors: {
          ...state.errors,
          validationErrors: {
            ...state.errors.validationErrors,
            [stepKey]: []
          }
        }
      };
    },
    // Set RFI loading status
    setRFIWizardStatus(state, action: PayloadAction<SetRFIWizardStatusParam>) {
      const { statusKey, status } = action.payload;
      return {
        ...state,
        status: {
          ...state.status,
          [statusKey]: status
        }
      };
    },
    // Set visibility of the RFI modals
    setRFIWizardVisibility(state, action: PayloadAction<SetRFIWizardVisibilityParam>) {
      const { visibilityKey, visibility } = action.payload;
      return {
        ...state,
        visibility: {
          ...state.visibility,
          [visibilityKey]: visibility
        }
      };
    },
    // Refresh the state for edit RFI. The wizard data will be set in next action
    refreshRFIForEdit(state, action: PayloadAction<{ rfiRequestForm: RFIRequestForm }>) {
      const { rfiRequestForm } = action.payload;
      return {
        ...initialState,
        requestAction: RFXRequestAction.EDIT_ROUND,
        isNewRequest: false,
        activeStepIndex: RFIWizardStep.Review,
        wizardData: rfiRequestForm
      };
    },
    onRFIInformationChange(state, action: PayloadAction<OnRFIInformationChangeParam>) {
      const { field, value } = action.payload;
      state.wizardData.generalInformation = {
        ...state.wizardData.generalInformation,
        [field]: value
      };
    },
    onRFICorrespondenceChange(state, action: PayloadAction<OnRFICorrespondenceChangeParam>) {
      const { field, value } = action.payload;
      state.wizardData.correspondence = {
        ...state.wizardData.correspondence,
        [field]: value
      };
    },
    onRFIDocumentsChange(state, action: PayloadAction<RequestOnChangeParam<'documents'>>) {
      const newFields = action.payload;
      return {
        ...state,
        wizardData: {
          ...state.wizardData,
          documents: {
            ...state.wizardData.documents,
            ...newFields
          }
        }
      };
    },
    onRFISuppliersChange(state, action: PayloadAction<RequestOnChangeParam<'suppliers'>>) {
      const newFields = action.payload;
      return {
        ...state,
        wizardData: {
          ...state.wizardData,
          suppliers: {
            ...state.wizardData.suppliers,
            ...newFields
          }
        }
      };
    },
    onRFICoManagersChange(state, action: PayloadAction<RequestOnChangeParam<'co-managers'>>) {
      const newFields = action.payload;
      return {
        ...state,
        wizardData: {
          ...state.wizardData,
          'co-managers': {
            ...state.wizardData['co-managers'],
            ...newFields
          }
        }
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(onRFINavigate.pending, (state) => {
      state.status.isLoadingNextStep = true;
    });
    builder.addCase(onRFINavigate.fulfilled, (state, action) => {
      state.status.isLoadingNextStep = false;
    });
    builder.addCase(onRFINavigate.rejected, (state, action) => {
      state.status.isLoadingNextStep = false;
      state.errors.navigationError = convertSystemErrorToString(action.error);
    });
  }
});

export interface OnLoadRFIRequestUIInput extends UIInput {
  rfxId: string;
}

/**
 * When buyer is editing RFI with the URL of buyer/view/rif/<rfxId>/edit, there could be 2 cases:
 * 1. buyer comes from details page, in which case the wizard data is well set and no further action is needed
 * 2. buyer types the URL in web browser directly, in which case they need to run this to set wizard data properly
 */
export const onResetRFIRequestForEdit = createAsyncThunk<
  void,
  OnLoadRFIRequestUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(`${SLICE_NAME}/onResetRFIRequestForEdit`, async ({ apolloClient, rfxId }, { dispatch }): Promise<void> => {
  try {
    dispatch(setRFIWizardStatus({ statusKey: 'isLoadingRFIForEdit', status: true }));

    const rfxRequest = await loadRFXRequest({ apolloClient, rfxId, revision: 1 });
    // TODO: catch convertion errors
    const rfiRequestForm = convertRFXRequestToRFIRequestForm(rfxRequest);
    dispatch(refreshRFIForEdit({ rfiRequestForm }));
  } catch (error) {
    const sanitizedError = convertSystemErrorToString(error);
    dispatch(setRFIWizardError({ errorKey: 'loadingRFIForEditError', error: sanitizedError }));
  } finally {
    dispatch(setRFIWizardStatus({ statusKey: 'isLoadingRFIForEdit', status: false }));
  }
});

export interface OnRFINaviageUIInput extends UIInput {
  detail: WizardProps.NavigateDetail;
}

export const onRFINavigate = createAsyncThunk<
  void,
  OnRFINaviageUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(`${SLICE_NAME}/onRFINavigate`, async (inputArgs, thunkApi): Promise<void> => {
  const { getState, dispatch } = thunkApi;
  const { reason, requestedStepIndex } = inputArgs.detail;

  const { activeStepIndex, isNewRequest, wizardData } = getState()[SLICE_NAME];

  const navigationHelper = new NavigationHelper({ inputArgs, thunkApi });

  if (reason === 'next') {
    // Logic for save and next

    // TODO: remove console log
    console.log('Active step: ' + activeStepIndex);
    console.log('Requested step: ' + requestedStepIndex);
    console.log('Navigation: ' + reason);

    switch (activeStepIndex) {
      case RFIWizardStep.GeneralInformation:
        const infoStepErrors = validateGeneralInformation(wizardData.generalInformation);
        if (infoStepErrors.length > 0) {
          dispatch(setRFIWizardStepError({ stepKey: 'generalInformationStep', errors: infoStepErrors }));
          return;
        }
        if (isNewRequest) {
          await navigationHelper.saveNew();
        } else {
          await navigationHelper.save();
        }
        break;
      case RFIWizardStep.Correspondence:
        const correspondencetepErrors = validateCorrespondence(wizardData.correspondence);
        if (correspondencetepErrors.length > 0) {
          dispatch(setRFIWizardStepError({ stepKey: 'correspondenceStep', errors: correspondencetepErrors }));
          return;
        }
        await navigationHelper.save();
        break;
      case RFIWizardStep.Documents:
        // Document step doesn't need to save data because attachment creation has updated the data.
        // Only need to refresh data to use the correct version, and changed data.
        await navigationHelper.refresh();
        break;
      case RFIWizardStep.InviteSuppliers:
        const supplierStepErrors = validateSuppliers(wizardData.suppliers.supplierEntries);
        if (supplierStepErrors.length > 0) {
          dispatch(setRFIWizardStepError({ stepKey: 'supplierStep', errors: supplierStepErrors }));
          return;
        }
        await navigationHelper.save();
        break;
      case RFIWizardStep.Review:
        break;
      default:
        await navigationHelper.save();
    }
  } else {
    // Toggle between steps
    await navigationHelper.refresh();
  }

  // ToDO: remove console log
  console.log(`set next step to ${requestedStepIndex}`);
  dispatch(setRFIActiveStepIndex(requestedStepIndex));
});

export const {
  setRFIActiveStepIndex,
  setRFIRequestState,
  setRFIIsNew,
  setRFIWizardData,
  setRFIWizardError,
  setRFIWizardStepError,
  clearRFIWizardStepError,
  setRFIWizardStatus,
  setRFIWizardVisibility,
  createNewRFIRequest,
  cloneRFIRequest,
  refreshRFIForEdit,
  onRFIInformationChange,
  onRFICorrespondenceChange,
  onRFIDocumentsChange,
  onRFISuppliersChange,
  onRFICoManagersChange
} = rfiWizardSlice.actions;

export const selectRFIActiveStepIndex = (state: RootState): number => {
  return state[SLICE_NAME].activeStepIndex;
};

export const selectRFIWizardData = (state: RootState): RFIRequestForm => {
  return state[SLICE_NAME].wizardData;
};

/**
 * Get validation error keys for all the RFI wizard steps.
 */
export const selectRFIValidationErrors = (state: RootState): RFIValidationErrors => {
  const { generalInformation, correspondence, suppliers } = state[SLICE_NAME].wizardData;
  const generalInfoErrors = validateGeneralInformation(generalInformation);
  const correspondenceErrors = validateCorrespondence(correspondence);
  const supplierErrors = validateSuppliers(suppliers.supplierEntries);
  return {
    generalInformationStep: generalInfoErrors,
    correspondenceStep: correspondenceErrors,
    supplierStep: supplierErrors
  };
};

/**
 * Select detailed validation errors for one RFI wizard step.
 *
 * Pre-condition: the wizard data has just been validated.
 */
export const selectRFIStepValidationErrors =
  ({ stepKey }: { stepKey: keyof RFIValidationErrors }) =>
  (state: RootState): RFIStepValidationErrors => {
    const stepErrorKeys = state[SLICE_NAME].errors['validationErrors'][stepKey];

    const errors = stepErrorKeys.map((key) => getErrorMessage(key));
    return {
      hasError: errors.length > 0,
      errors
    };
  };

export default rfiWizardSlice.reducer;
